import React, { useState } from "react";

/**
 * A reusable table component for displaying and editing parameter inputs.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.parameters - Array of parameter input objects
 * @param {Object} props.values - Current values for parameters
 * @param {Function} props.onValueChange - Function to call when a value changes
 * @param {Function} props.formatValue - Optional function to format display values
 */
const ParameterTable = ({ parameters, values, onValueChange, formatValue = (val) => val }) => {
  // Track raw input values before parsing to numbers
  const [inputValues, setInputValues] = useState({});
  
  // Format currency with thousands separator and no decimals
  const defaultFormatCurrency = (value) => {
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Handle input change
  const handleInputChange = (id, value) => {
    // Allow numeric values, decimal points, and empty strings
    const isValidInput = /^[0-9]*\.?[0-9]*$/.test(value) || value === '';
    
    if (isValidInput) {
      // Store the raw input first
      setInputValues(prev => ({
        ...prev,
        [id]: value
      }));
      
      // Convert to number for the actual parameter
      const paramValue = value === '' ? 0 : parseFloat(value);
      
      // Only update if it's a valid number
      if (!isNaN(paramValue)) {
        onValueChange(id, paramValue);
      }
    }
  };

  // Get the displayed value for an input
  const getDisplayValue = (input, currentValue) => {
    const isPrice = input.id.includes('price');
    const isDecimalField = !isPrice;
    
    // If we have a raw input value, use that
    if (inputValues[input.id] !== undefined) {
      return inputValues[input.id];
    }
    
    // Otherwise format based on field type
    if (isPrice) {
      return defaultFormatCurrency(currentValue);
    } else if (isDecimalField) {
      return currentValue.toString();
    } else {
      return formatValue(currentValue);
    }
  };

  return (
    <div className="table-container">
      <table className="parameter-selection table">
        <thead>
          <tr>
            <th style={{ width: '25%' }}>Parameter</th>
            <th style={{ width: '15%' }}>Value</th>
            <th style={{ width: '60%' }}>Description</th>
          </tr>
        </thead>
        <tbody>
          {parameters.map((input) => {
            const currentValue = values?.[input.id] ?? input.defaultValue;
            const isPrice = input.id.includes('price');
            
            return (
              <tr key={input.id}>
                <td>{input.label}</td>
                <td>
                  <div className="editable-cell">
                    {isPrice && <span className="currency-symbol">$</span>}
                    <input
                      type="text"
                      value={getDisplayValue(input, currentValue)}
                      onChange={(e) => handleInputChange(input.id, e.target.value)}
                      className="incentive-input"
                    />
                  </div>
                </td>
                <td>{input.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ParameterTable; 